import { useEffect, useState } from 'react';
import { apiGet, apiPostFull } from '../api/client';
import type { Invoice, ShopifyOrder } from '../types';
import { formatMoney } from '../utils/currency';
import { getEffectiveInvoiceStatus } from '../utils/invoiceStatus';
import { t } from '../i18n';

interface OrdersProps {
  onError: (error: string | null) => void;
}

const PAGE_SIZES = [5, 10, 20];

function isActiveRootInvoiceForOrder(invoice: Invoice, orderId: string): boolean {
  return invoice.platform_order_id === orderId
    && invoice.original_invoice_id === null
    && getEffectiveInvoiceStatus(invoice) !== 'cancelled';
}

export function Orders({ onError }: OrdersProps) {
  const [orderId, setOrderId] = useState('');
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState<Invoice | null>(null);
  const [alreadyExisted, setAlreadyExisted] = useState(false);
  const [orders, setOrders] = useState<ShopifyOrder[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  useEffect(() => {
    Promise.all([
      apiGet<ShopifyOrder[]>('/orders?limit=100'),
      apiGet<Invoice[]>('/invoices?limit=100'),
    ])
      .then(([orderData, invoiceData]) => {
        setOrders(orderData);
        setInvoices(invoiceData);
        onError(null);
      })
      .catch((err) => onError(`${t('orders_load_error')}: ${err instanceof Error ? err.message : t('unknown_error')}`))
      .finally(() => setLoadingOrders(false));
  }, [onError]);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const totalPages = Math.max(1, Math.ceil(orders.length / pageSize));
  const paged = orders.slice((page - 1) * pageSize, page * pageSize);

  async function createInvoiceForOrder(targetOrderId: string) {
    if (!targetOrderId.trim()) return;

    const existingInvoice = invoices.find((invoice) => isActiveRootInvoiceForOrder(invoice, targetOrderId.trim()));
    if (existingInvoice && !['draft', 'validation_failed'].includes(existingInvoice.status)) {
      setLastCreated(existingInvoice);
      setAlreadyExisted(true);
      setOrderId('');
      onError(null);
      return;
    }

    setCreating(true);
    setLastCreated(null);
    setAlreadyExisted(false);
    try {
      const result = await apiPostFull<Invoice>(`/orders/${encodeURIComponent(targetOrderId.trim())}/create-invoice`);
      setLastCreated(result.data);
      setAlreadyExisted(!!result.already_exists);
      setOrderId('');
      onError(null);
    } catch (err) {
      onError(`${t('issue_error')} ${err instanceof Error ? err.message : t('unknown_error')}`);
    } finally {
      setCreating(false);
    }
  }

  async function handleCreateInvoice(e: React.FormEvent) {
    e.preventDefault();
    await createInvoiceForOrder(orderId);
  }

  return (
    <div className="page-container">
      <div className="section-header">
        <h1 className="section-title">{t('orders_title')}</h1>
      </div>

      {lastCreated && (
        <div className={`banner mb-4 ${alreadyExisted ? 'banner--warning' : 'banner--success'}`}>
          <span className="banner-content">
            <strong>#{lastCreated.invoice_number}</strong>{' '}
            {alreadyExisted ? t('invoice_already_exists') : t('invoice_created')}{' '}
            <span className="text-sm text-secondary">{t('invoice_created_hint')}</span>
          </span>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title">{t('latest_shopify_orders')}</h3>
        </div>

        {loadingOrders ? (
          <p className="text-secondary">{t('orders_loading')}</p>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">{t('no_orders_found')}</div>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>{t('col_order')}</th>
                    <th>{t('col_customer')}</th>
                    <th>{t('col_total')}</th>
                    <th>{t('col_status')}</th>
                    <th>{t('col_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((order) => {
                    const isReversedOrder = ['refunded', 'voided'].includes(order.financial_status);
                    const existingInvoice = invoices.find((invoice) => isActiveRootInvoiceForOrder(invoice, order.id));

                    return (
                      <tr key={order.id}>
                        <td className="fw-medium">{order.name || `#${order.id}`}</td>
                        <td className="text-secondary truncate" style={{ maxWidth: 200 }}>{order.customer?.company?.name || [order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(' ') || order.email || '—'}</td>
                        <td className="fw-medium">{formatMoney(order.total_price, order.currency)}</td>
                        <td>
                          {formatFinancialStatus(order.financial_status)}
                          {isReversedOrder && (
                            <div className="text-sm text-secondary" style={{ marginTop: 4, maxWidth: 320 }}>
                              {t('reversed_order_hint')}
                            </div>
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            disabled={creating}
                            onClick={() => void createInvoiceForOrder(order.id)}
                            className="btn btn--tertiary btn--sm"
                          >
                            {existingInvoice ? t('invoice_already_created') : t('create_invoice')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              total={orders.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">{t('create_invoice_from_order')}</h3>
        </div>
        <form onSubmit={handleCreateInvoice} className="flex items-center gap-3">
          <div className="form-group" style={{ marginBottom: 0, flex: '0 0 220px' }}>
            <input
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder={t('order_id_placeholder')}
              disabled={creating}
              className="form-input"
            />
          </div>
          <button
            type="submit"
            disabled={creating || !orderId.trim()}
            className="btn btn--primary"
          >
            {creating ? t('creating') : t('create_invoice')}
          </button>
        </form>
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, total, pageSize, onPageChange, onPageSizeChange }: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (s: number) => void;
}) {
  return (
    <div className="pagination">
      <span className="flex items-center gap-2">
        {total} {t('entries')}
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="pagination-select"
        >
          {PAGE_SIZES.map(s => <option key={s} value={s}>{s}{' ' + t('per_page')}</option>)}
        </select>
      </span>
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="pagination-btn">{'<'}</button>
          <span className="text-sm">{page} / {totalPages}</span>
          <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className="pagination-btn">{'>'}</button>
        </div>
      )}
    </div>
  );
}

function formatFinancialStatus(status: string): React.ReactNode {
  const labels: Record<string, string> = {
    paid: t('financial_paid'),
    pending: t('financial_pending'),
    authorized: t('financial_authorized'),
    partially_paid: t('financial_partially_paid'),
    refunded: t('financial_refunded'),
    voided: t('financial_voided'),
  };

  const badgeClass: Record<string, string> = {
    paid: 'success',
    pending: 'warning',
    authorized: 'info',
    partially_paid: 'warning',
    refunded: 'neutral',
    voided: 'critical',
  };

  return (
    <span className={`badge badge--${badgeClass[status] || 'neutral'}`}>
      {labels[status] || status || '—'}
    </span>
  );
}
